import type { Entity } from '@backstage/catalog-model';

export type JsonObject = NonNullable<Entity['spec']>;

export type StackOperation = 'create' | 'update' | 'action' | 'delete';
export type StackOperationOutcome =
  | 'pending'
  | 'running'
  | 'succeeded'
  | 'failed'
  | 'cancelled';

export type StackCapability =
  | 'console'
  | 'graph'
  | 'monitoring'
  | 'storage-lifecycle';

export type StackRemoteAccessIntegration = {
  protocol: 'ssh' | 'rdp';
  host: string;
  port: number;
  username: string;
};

export type StackIntegrations = {
  grafana?: JsonObject;
  remoteAccess?: StackRemoteAccessIntegration;
  storageLifecycle?: { tenant: string };
};

export function stackGrafanaIntegration(
  entity: Entity,
): JsonObject | undefined {
  if (!isStackV1Entity(entity)) return undefined;
  const descriptor = entity.spec.integrations?.grafana;
  return descriptor && typeof descriptor === 'object' ? descriptor : undefined;
}

export type StackLastOperation = JsonObject & {
  type: StackOperation;
  outcome: StackOperationOutcome;
  jobId: string;
  startedAt?: string;
  finishedAt?: string;
  /**
   * Why the operation failed, as reported by the job that ran it. Present
   * only on a failed outcome, and only once the orchestrator has a reason to
   * give - a job can fail without one.
   */
  error?: string;
};

export type StackV1Spec = JsonObject & {
  owner: string;
  tenant: string;
  stackType: string;
  capabilities?: StackCapability[];
  integrations?: StackIntegrations;
  applicationGroup: string;
  applicationGroupId: string;
  destination: string;
  parameters: JsonObject;
  lifecycleState: string;
  lastOperation?: StackLastOperation;
  outputs: JsonObject;
  createdAt: string;
  updatedAt: string;
};

export function stackRemoteAccess(
  entity: Entity,
): StackRemoteAccessIntegration | undefined {
  if (!isStackV1Entity(entity)) return undefined;
  const descriptor = entity.spec.integrations?.remoteAccess;
  if (
    !descriptor ||
    !['ssh', 'rdp'].includes(descriptor.protocol) ||
    typeof descriptor.host !== 'string' ||
    descriptor.host.length === 0 ||
    typeof descriptor.port !== 'number' ||
    !Number.isInteger(descriptor.port) ||
    descriptor.port <= 0 ||
    descriptor.port > 65535 ||
    typeof descriptor.username !== 'string' ||
    descriptor.username.length === 0
  ) {
    return undefined;
  }
  return descriptor;
}

export function stackStorageLifecycleTenant(
  entity: Entity,
): string | undefined {
  if (!isStackV1Entity(entity)) return undefined;
  const tenant = entity.spec.integrations?.storageLifecycle?.tenant;
  return tenant === entity.spec.tenant ? tenant : undefined;
}

export type StackV1Entity = Entity & {
  apiVersion: 'aeven.io/v1alpha1';
  kind: 'Stack';
  spec: StackV1Spec;
};

export type StackOperationTemplateEntity = Entity & {
  kind: 'Template';
  spec: JsonObject & {
    type: 'stack-operation';
    stackType: string;
    operation: StackOperation;
  };
};

export function isStackV1Entity(entity: Entity): entity is StackV1Entity {
  const spec = entity.spec as Partial<StackV1Spec> | undefined;
  return (
    entity.apiVersion === 'aeven.io/v1alpha1' &&
    entity.kind === 'Stack' &&
    typeof spec?.tenant === 'string' &&
    typeof spec.stackType === 'string' &&
    typeof spec.applicationGroup === 'string' &&
    typeof spec.lifecycleState === 'string'
  );
}

export function isStackOperationTemplate(
  entity: Entity,
): entity is StackOperationTemplateEntity {
  const spec = entity.spec as
    | { type?: unknown; stackType?: unknown; operation?: unknown }
    | undefined;
  return (
    entity.kind === 'Template' &&
    spec?.type === 'stack-operation' &&
    typeof spec.stackType === 'string' &&
    spec.stackType.length > 0 &&
    ['create', 'update', 'action', 'delete'].includes(String(spec.operation))
  );
}

/**
 * Discover workflows by typed taxonomy. `metadata.name` is deliberately never
 * reconstructed; callers navigate with the identity on the returned entity.
 */
export function stackOperationTemplatesFor(
  stack: StackV1Entity,
  templates: Entity[],
): StackOperationTemplateEntity[] {
  const namespace = stack.metadata.namespace ?? stack.spec.tenant;
  return templates
    .filter(isStackOperationTemplate)
    .filter(
      template =>
        (template.metadata.namespace ?? 'default') === namespace &&
        template.spec.stackType === stack.spec.stackType,
    )
    .sort((left, right) => {
      const operation = left.spec.operation.localeCompare(right.spec.operation);
      if (operation !== 0) return operation;
      const defaultName = `${stack.spec.stackType}-${left.spec.operation}`;
      if (left.metadata.name === defaultName) return -1;
      if (right.metadata.name === defaultName) return 1;
      return left.metadata.name.localeCompare(right.metadata.name);
    });
}

/**
 * Status used for presentation and existing Scaffolder operation forms. The
 * deployed lifecycle remains in `lifecycleState`; an in-flight or failed Job
 * is represented from `lastOperation` without overwriting that stable state.
 */
export function stackStatusOf(entity: StackV1Entity): string {
  const operation = entity.spec.lastOperation;
  if (operation?.outcome === 'pending' || operation?.outcome === 'running') {
    switch (operation.type) {
      case 'create':
        return 'CREATING';
      case 'update':
        return 'UPDATING';
      case 'action':
        return 'ACTION_RUNNING';
      case 'delete':
        return 'DESTROYING';
      default:
        break;
    }
  }
  if (operation?.outcome === 'failed') return 'FAILED';
  if (operation?.outcome === 'cancelled') return 'CANCELLED';
  return entity.spec.lifecycleState.toLocaleUpperCase('en-US');
}

export function isStackOperationInProgress(entity: StackV1Entity): boolean {
  return isStackSpecOperationInProgress(entity.spec);
}

export function isStackSpecOperationInProgress(
  spec: Pick<StackV1Spec, 'lastOperation'>,
): boolean {
  const outcome = spec.lastOperation?.outcome;
  return outcome === 'pending' || outcome === 'running';
}

export function isStackEditable(entity: StackV1Entity): boolean {
  return isStackSpecEditable(entity.spec);
}

export function isStackSpecEditable(
  spec: Pick<StackV1Spec, 'lifecycleState' | 'lastOperation'>,
): boolean {
  return (
    !isStackSpecOperationInProgress(spec) &&
    ['ready', 'failed'].includes(spec.lifecycleState)
  );
}

export function isStackDeletingOrDeleted(entity: StackV1Entity): boolean {
  return (
    entity.spec.lifecycleState === 'destroyed' ||
    (entity.spec.lastOperation?.type === 'delete' &&
      ['pending', 'running', 'succeeded'].includes(
        entity.spec.lastOperation.outcome,
      ))
  );
}

/** The Orchestrator contract already supplies a fully-qualified entity ref. */
export function stackApplicationGroupRef(entity: StackV1Entity): string {
  return entity.spec.applicationGroup;
}
